import React, { useState, useMemo, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FormControl, TextField, FormHelperText } from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';
import debounce from 'lodash.debounce';

// Configure nunjucks with custom delimiters.
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const CreateAdoApp = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;

  // internalValue holds the text currently displayed in the input.
  const [internalValue, setInternalValue] = useState(formData || '');
  // validationError is used to show errors in the UI.
  const [validationError, setValidationError] = useState<string | null>(null);

  // Access the identityApi to retrieve credentials.
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [baseUrl, setBaseUrl] = useState('');

  useEffect(() => {
    const fetchBaseUrl = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      setBaseUrl(baseUrl);
    };
    fetchBaseUrl();
  }, [discoveryApi]);

  // Render the ADO project and product templates using the current form context.
  // These values are typically provided in the uiSchema under "ui:options".
  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';
  const resolvedAdoProject = nunjucks
    .renderString(adoProjectTemplate, formContext.formData)
    .trim();

  const adoProductTemplate = uiSchema['ui:options']?.adoProduct || '';
  const resolvedAdoProduct = nunjucks
    .renderString(adoProductTemplate, formContext.formData)
    .trim();

  console.log('CreateAdoApp: resolved adoProject =', resolvedAdoProject);
  console.log('CreateAdoApp: resolved adoProduct =', resolvedAdoProduct);

  /**
   * checkAppExists:
   * Fetches the list of apps from AWS Parameter Store for the resolved ADO project and product,
   * and returns true if the app already exists.
   */
  const checkAppExists = async (value: string): Promise<boolean> => {
    if (!resolvedAdoProject || !resolvedAdoProduct) {
      // If project or product values are missing, skip validation.
      return false;
    }
    try {
      const credentials = await identityApi.getCredentials();
      const token = credentials.token;
      const response = await axios.post(
        `${baseUrl}/aws/params`,
        { name: `/backstage/project/${resolvedAdoProject}/${resolvedAdoProduct}` },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const rawValue = response.data.value || '';
      let appList: string[] = [];
      if (rawValue) {
        const parsed = yaml.load(rawValue);
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).apps)) {
          appList = (parsed as any).apps;
        } else if (typeof parsed === 'string') {
          appList = parsed
            .split(',')
            .map((p: string) => p.trim())
            .filter((p: string) => p);
        }
      }
      return appList.includes(value);
    } catch (error) {
      console.error('Error checking app existence:', error);
      return false;
    }
  };

  // Debounced async validation function.
  // It first checks whether the field is empty, then verifies if the app is already present.
  // If validation passes, it updates the parent's value; otherwise, it leaves it blank.
  const debouncedValidate = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value.trim()) {
          setValidationError('App field is required.');
          onChange('');
          return;
        }
        const exists = await checkAppExists(value);
        if (exists) {
          setValidationError('App already exists in AWS Parameter Store.');
          onChange('');
        } else {
          setValidationError(null);
          onChange(value);
        }
      }, 800),
    [onChange, resolvedAdoProject, resolvedAdoProduct]
  );

  // On every keystroke, update the internal value and immediately clear the parent's value.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInternalValue(value);
    // Immediately clear the parent's value to avoid stale data.
    onChange('');
    
    // Check if value contains only lowercase letters
    if (value && !/^[a-z]*$/.test(value)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      return;
    }
    
    setValidationError(null);
    debouncedValidate(value);
  };

  // On blur, flush any pending debounced validation and perform an immediate check.
  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    debouncedValidate.cancel();
    const value = event.target.value.trim();
    if (!value) {
      setValidationError('App field is required.');
      onChange('');
      return;
    }
    
    // Check if value contains only lowercase letters
    if (!/^[a-z]+$/.test(value)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      onChange('');
      return;
    }
    
    const exists = await checkAppExists(value);
    if (exists) {
      setValidationError('App already exists in AWS Parameter Store.');
      onChange('');
    } else {
      setValidationError(null);
      onChange(value);
    }
  };

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!validationError}>
      <TextField
        required={required}
        label={schema.title || 'App Name'}
        helperText={validationError || schema.description}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!rawErrors?.length || !!validationError}
      />
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
